import React, { useEffect, useRef, useState, FC } from "react";

import { useParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import fruitgame from "../assets/images/games/fruit-party-tile-auth.jpg";
import Breadcrumb from "./BreadCrumb";
import GameActionButtons from "./GameActionButtons";
import Footer from "../common/Footer";
import Header from "../common/header";
import Subbanner from "../common/Subbanner";
import gamebg from "../assets/images/play-game-bg.jpg";
import { fetchGamesByCategory, trackGuestPlayAPI } from "../api/html5GamesApi";
import { getUserIP, getBrowserData } from "../utils/userMeta";
import { getFingerprint } from "../utils/fingerprint";
import { url } from "../api/url";
import RoundLoader from "../common/RoundLoader";
import GoogleAds from "../component/GoogleAds/GoogleAds";
// Types
interface Game {
  _id: string;
  name: string;
  description?: string;
  rating?: number;
  votes?: number;
  release?: string;
  tech?: string;
  totalJoinedPlayers?: number;
  url: string;
  thumbImage?: {
    default?: string;
  };
  html5Category?: {
    _id: string;
    name: string;
    slug?: string;
  };
}

interface LocationState {
  game?: Game;
}


interface RouteParams {
  slug: string;
}

// Slug conversion
const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Debounce utility
const debounce = (fn: () => void, delay = 100) => {
  let timer: NodeJS.Timeout;
  return (...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(), delay);
  };
};

const GameDetail: React.FC = () => {
  const { t } = useTranslation();

  // ✅ UseParams without type param
  const params = useParams();
  const slug = params.slug as string; // Safely assert slug exists

  // ✅ UseLocation and cast state
  const location = useLocation();
  const passedGame = (location.state as LocationState)?.game;

  // ✅ State declarations
  const [game, setGame] = useState<Game | null>(passedGame || null);
  const [relatedGames, setRelatedGames] = useState<Game[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [leftHeight, setLeftHeight] = useState<number>(0);

  const hasCategory = (
    game: Game
  ): game is Game & { html5Category: { _id: string; name: string } } => {
    return !!(game.html5Category && game.html5Category._id && game.html5Category.name);
  };
  // ✅ useRef with null default
  const leftRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchGameDetails = async () => {
      let currentGame = passedGame;

      if (!currentGame) {
        const allGamesResp = await fetchGamesByCategory("");
        currentGame = allGamesResp?.list?.find((g: Game) => toSlug(g.name) === slug);
      }

      if (currentGame) {
        setGame(currentGame);

        const relatedRes = await fetchGamesByCategory(currentGame.html5Category?._id || "");
        if (relatedRes?.list) {
          const filtered = relatedRes.list.filter((g: Game) => toSlug(g.name) !== slug);
          setRelatedGames(filtered);
        }
      }

      setInitialLoading(false);
    };

    fetchGameDetails();
  }, [slug, passedGame]);

  useEffect(() => {
    const updateHeight = () => {
      if (leftRef.current) {
        setLeftHeight(leftRef.current.offsetHeight);
      }
    };

    updateHeight();

    const handleResize = debounce(updateHeight, 150);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [game, relatedGames]);

  const formatNumber = (num?: number): string => {
    if (!num) return "-";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
    return num.toString();
  };

  const handlePlayNow = () => {
    if (!game || !hasCategory(game)) return;

    const newTab = window.open(game.url, "_blank");
    if (!newTab) {
      alert("Please allow pop-ups for this site to play the game.");
      return;
    }

    (async () => {
      try {
        const ip = await getUserIP();
        const browser = getBrowserData();
        const fingerprint = await getFingerprint();

        const payload = {
          gameId: game._id,
          gameName: game.name,
          categoryId: game.html5Category._id,
          categoryName: game.html5Category.name,
          status: "start",
          ip,
          browser,
          fingerprint,
        };

        await trackGuestPlayAPI(payload);
      } catch (err) {
        console.error("Error tracking guest play:", err);
      }
    })();
  };

  if (initialLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex justify-center items-center">
        <RoundLoader size={10} color="#f92c2c" borderWidth={5} />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center text-red-600 p-10">
        ⚠️ {t("gameDetail.notFound")}
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#0E122B]">
      <Header />
      <Subbanner height="30vh" />

      <div
        className="flex-1 py-16 pt-[2rem] px-4 bg-cover bg-no-repeat bg-center font-proxima"
        style={{ backgroundImage: `url(${gamebg})`, background: "#070B28" }}
      >
        <div className="max-w-[1316px] mx-auto grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-6 items-start">
          {/* LEFT */}
          <div ref={leftRef} className="overflow-hidden h-fit">
            <div id="game-preview" className="relative w-full rounded-lg overflow-hidden h-fit" style={{ paddingTop: "56.25%" }}>
              <img
                src={
                  game?.thumbImage?.default
                    ? `${url.imgUrl}/${game.thumbImage.default}`
                    : fruitgame
                }
                alt={game?.name || "-"}
                className="absolute top-0 left-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none rounded-b-lg" />
              <button
                onClick={handlePlayNow}
                className="cursor-pointer absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-semibold text-white"
              >
                {t("gameDetail.playNow")}
              </button>
            </div>
            <div className="mt-[1rem] bg-[#070B28] rounded-lg overflow-hidden h-fit" style={{ border: "0.1px solid white"}}>   
            <div className="px-4 py-3 border-t border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-white text-lg font-semibold">{game?.name || "-"}</h2>
          <GameActionButtons gameTitle={""} gameId={game._id} />

            </div>

            <div className="px-4 pb-6 border-t border-gray-700">
              <Breadcrumb category={game?.html5Category} gameName={game?.name} />
              <div className="mt-2 text-sm text-gray-400 space-y-2">
                <div className="flex items-start gap-7">
                  <strong className="w-28 text-white">{t("gameDetail.category")}:</strong>
                  <span>{game?.html5Category?.name || "-"}</span>
                </div>
                <div className="flex items-start gap-7">
                  <strong className="w-28 text-white">{t("gameDetail.rating")}:</strong>
                  <span>
                    {game?.rating} ({game?.votes} votes)
                  </span>
                </div>
                <div className="flex items-start gap-7">
                  <strong className="w-28 text-white">{t("gameDetail.gameplays")}:</strong>
                  <span>{formatNumber(game.totalJoinedPlayers)}</span>
                </div>
                <div className="flex items-start gap-7">
                  <strong className="w-28 text-white">{t("gameDetail.released")}:</strong>
                  <span>{game?.release}</span>
                </div>
                <div className="flex items-start gap-7">
                  <strong className="w-28 text-white">{t("gameDetail.technology")}:</strong>
                  <span>{game?.tech}</span>
                </div>
              </div>
            </div>

            <div className="px-4 pb-6 border-t border-gray-700">
              <p className="mt-6 text-white">
                {game?.description || t("gameDetail.noDescription")}
              </p>
            </div>
          </div>
</div> 
          {/* RIGHT */}
          <div className="h-full flex flex-col gap-6">
            <div className="bg-[#070B28] p-2 rounded-lg text-center text-white" style={{ border: "1.5px solid grey"}}>
            
                  <GoogleAds
                    adUnitPath="/157165500,23206251223/MCM_Gj_Console/MCM_Gj_Console_interstitial_1"
                    sizes={[[300, 250], [320, 480], [336, 280]]}
                    divId="mcm_console_interstitial_1"
                  />
            </div>

            <div
              className="bg-white rounded-lg text-white overflow-y-auto scroll-smooth scrollbar-hide"
              style={{ maxHeight: leftHeight || "auto", minHeight: 200 , background: "#070B28"}}
            >
              <h3 className="text-sm font-semibold mb-3 text-[#ffffff] p-3" style={{ background: "#070B28"}}>
                {t("gameDetail.moreGames")}
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 gap-2 px-3 pb-3" style={{ background: "#070B28"}}>
                {relatedGames.map((g) => (
                  <Link
                    to={`/game/${toSlug(g.name)}`}
                    key={g._id}
                    state={{ game: g }}
                    className="relative overflow-hidden rounded-lg pt-[100%]"
                  >
                    <img
                      src={`${url.imgUrl}/${g.thumbImage?.default || ""}`}
                      alt={g.name}
                      className="absolute top-0 left-0 w-full h-full object-cover transition-transform hover:scale-105"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
         <div className="mt-4" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <div className="col-12 my-3 d-flex justify-content-center">
                      <GoogleAds
                        adUnitPath="/157165500,23206251223/MCM_Gj_Console/MCM_Gj_Console_banner_1"
                        sizes={[[728, 90], [336, 280]]}
                        divId={`gpt-banner-1`}
                      />
                    </div>
                  </div>
      </div>
    

      <Footer />
    </div>
  );
};

export default GameDetail;
